import React, { useEffect } from 'react'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Title,
  Tooltip as ChartJSTooltip,
} from 'chart.js'
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm'
import {
  DataSource,
  Panel,
  PanelType,
  SingleValueDataKeySpecificProps,
} from '../../../../shared/models/panel'
import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Flex from '../../../../components/common/Flex'
import PanelMenu from '../PanelMenu'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector } from 'react-redux'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../../../store/store'
import {
  AggKey,
  BaseTimeseriesCmd,
  isLatestTimeseriesCmd,
  isTimeseriesAggCmd,
  isTimeseriesCmd,
  isTimeseriesHistoryAggCmd,
  isTimeseriesHistoryCmd,
  LatestTimeseriesCmd,
  QueryType,
  TimeseriesAggCmd,
  TimeseriesCmd,
  TimeseriesHistoryAggCmd,
  TimeseriesHistoryCmd,
  useLazyGetAggTimeSeriesQuery,
  useLazyGetLatestTimeSeriesQuery,
  useLazyGetTimeSeriesQuery,
} from '../../../../store/kopiliot-api/time-series'
import _ from 'lodash'
import { toast } from 'react-toastify'
import GaugeList from '../chart-helpers/gauge/GaugeList'
import {
  AggregationType,
  getStartAndEndTsByHistoryWindow,
  getStartAndTWByRealtimeWindow,
  TimeWindow,
  TimeWindowType,
} from '../../../../shared/models/time.model'
import {
  AggregatedTimeseriesData,
  LatestTimeseriesDataWithPrevious,
  TimeseriesData,
} from '../../../../shared/models/telemetry/telemetry.model'
import TimeSeriesLineChart from '../chart-helpers/line/TimeSeriesLineChart'
import Spinner from '../../../../components/spinner/Spinner'
import SingleValueList from '../chart-helpers/single-value/SingleValueList'
import TimeSeriesTable from '../chart-helpers/table/time-series-table/TimeSeriesTable'
import JsonTable from '../chart-helpers/table/json-table/JsonTable'
import TimeSeriesBarChart from '../chart-helpers/bar/TimeSeriesBarChart'
import TimeSeriesPieChart from '../chart-helpers/pie/TimeSeriesPieChart'
import TimeSeriesPolarChart from '../chart-helpers/polar/TimeSeriesPolarChart'
import dayjs from 'dayjs'
import SimpleIndicatorList from '../chart-helpers/simple-indicator/SimpleIndicatorList'
import { websocketSendQuery } from '../../../../store/slices/websocket'
import { v4 as uuidV4 } from 'uuid'
import { Alias } from '../../../../shared/models/alias.model'

interface Props {
  panel?: Panel
  isMouseIn?: boolean
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartJSTooltip,
  Legend,
  TimeSeriesScale,
  TimeScale,
  RadialLinearScale
)

interface QueryState {
  query: BaseTimeseriesCmd<any>
  datasource: DataSource
  alias: Alias
}

export const PanelBody = ({ panel, isMouseIn }: Props) => {
  const { isConnected } = useAppSelector((state) => state.websocket)
  const [queryId] = React.useState<string>(uuidV4())
  const {
    config,
    useStorageTsV2,
    aliases = {},
  } = useSelector((state: RootState) => state.dashboard)
  const [queries, setQueries] = React.useState<QueryState[]>([])
  const dispatch = useAppDispatch()
  useEffect(() => {
    if (isConnected && queryId) {
      dispatch(
        websocketSendQuery({
          queryID: queryId,
          query: 'your_query_here',
        })
      )
    }
  }, [dispatch, isConnected])

  const [timeSeriesData, setTimeSeriesData] = React.useState<{
    [queryID: string]: { datasource: DataSource; data: TimeseriesData }
  }>({})
  const [latestData, setLatestData] = React.useState<{
    [queryID: string]: {
      datasource: DataSource
      data: LatestTimeseriesDataWithPrevious
    }
  }>({})
  const [aggregatedData, setAggregatedData] = React.useState<{
    [queryID: string]: {
      datasource: DataSource
      data: AggregatedTimeseriesData
    }
  }>({})
  // Getting timeseries data
  const [
    getTelemetries,
    { isLoading: isTimeseriesLoading, isFetching: isTimeseriesFetching },
  ] = useLazyGetTimeSeriesQuery()
  const [
    getLatestTelemetries,
    { isLoading: isLatestLoading, isFetching: isLatestFetching },
  ] = useLazyGetLatestTimeSeriesQuery()
  const [
    getAggTelemetries,
    { isLoading: isAggLoading, isFetching: isAggFetching },
  ] = useLazyGetAggTimeSeriesQuery()
  const isLoading =
    isTimeseriesLoading ||
    isTimeseriesFetching ||
    isAggLoading ||
    isAggFetching ||
    isLatestLoading ||
    isLatestFetching

  const buildAggregateQuery = (
    dataSource: DataSource<SingleValueDataKeySpecificProps>,
    index: number,
    alias: Alias,
    timeWindow: TimeWindow
  ): TimeseriesAggCmd | TimeseriesHistoryAggCmd | null => {
    const { entityAliasID } = dataSource
    if (!entityAliasID) {
      return null
    }
    const {
      config: { singleEntity },
    } = aliases[entityAliasID.id]
    if (!singleEntity || !singleEntity.entityType || !singleEntity.id) {
      return null
    }
    const id = `${alias.id?.id}_${index}`
    if (
      timeWindow.timeWindowType === TimeWindowType.REAL_TIME &&
      timeWindow.realtime
    ) {
      const { startTs, timeWindowMs } = getStartAndTWByRealtimeWindow(
        timeWindow.realtime
      )
      const query: TimeseriesAggCmd = {
        id,
        type: QueryType.REAL_TIME,
        keys: [],
        entityID: singleEntity,
        startTs: startTs,
        timeWindowMs: timeWindowMs,
        aggregated: true,
      }
      query.keys = _.map(dataSource.dataKeys, (k) => {
        const key: AggKey = {
          key: k.key,
          aggregationType: k.specificProps?.strategy || AggregationType.SUM,
        }
        if (k.specificProps?.enableDelta) {
          key.previousValueOnly = false
          key.previousStartTs =
            k.specificProps?.comparisonPeriod === 'previousInterval' &&
            timeWindow.realtime?.timeWindowsMs
              ? dayjs().valueOf() - timeWindow.realtime?.timeWindowsMs * 2
              : undefined
          key.previousEndTs =
            k.specificProps?.comparisonPeriod === 'previousInterval' &&
            timeWindow.realtime?.timeWindowsMs
              ? dayjs().valueOf() - timeWindow.realtime?.timeWindowsMs
              : undefined
        }
        return key
      })
      return query
    } else if (
      timeWindow.timeWindowType === TimeWindowType.HISTORY &&
      timeWindow.history
    ) {
      const { startTs, endTs } = getStartAndEndTsByHistoryWindow(
        timeWindow.history
      )
      const query: TimeseriesHistoryAggCmd = {
        id,
        type: QueryType.HISTORY,
        keys: [],
        entityID: singleEntity,
        startTs: startTs,
        endTs: endTs,
        aggregated: true,
      }
      query.keys = _.map(dataSource.dataKeys, (k) => {
        const key: AggKey = {
          key: k.key,
          aggregationType: k.specificProps?.strategy || AggregationType.SUM,
        }
        if (k.specificProps?.enableDelta) {
          key.previousValueOnly = false
          key.previousStartTs =
            k.specificProps?.comparisonPeriod === 'previousInterval'
              ? startTs - (endTs - startTs)
              : undefined
          key.previousEndTs =
            k.specificProps?.comparisonPeriod === 'previousInterval'
              ? startTs
              : undefined
        }
        return key
      })
      return query
    }
    return null
  }
  const buildLatestQuery = (
    dataSource: DataSource<any>,
    alias: Alias
  ): LatestTimeseriesCmd | null => {
    if (
      !alias.config.singleEntity?.entityType &&
      !alias.config.singleEntity?.id
    ) {
      return null
    }
    if (dataSource.latestDataKeys?.length === 0) {
      return null
    }
    const id = `${alias.id?.id}_LATEST`
    return {
      id: id,
      type: QueryType.LATEST,
      entityID: alias.config.singleEntity,
      keys: _.map(dataSource.latestDataKeys, (k) => k.key),
    }
  }
  const buildQuery = (
    dataSource: DataSource<any>,
    index: number,
    alias: Alias,
    timeWindow: TimeWindow
  ): TimeseriesCmd | TimeseriesHistoryCmd | null => {
    if (
      !alias.config.singleEntity?.entityType &&
      !alias.config.singleEntity?.id
    ) {
      return null
    }
    const id = `${alias.id?.id}_${index}`
    if (
      timeWindow.timeWindowType === TimeWindowType.REAL_TIME &&
      timeWindow.realtime
    ) {
      const { startTs, timeWindowMs } = getStartAndTWByRealtimeWindow(
        timeWindow.realtime
      )
      return {
        id: id,
        type: QueryType.REAL_TIME,
        entityID: alias.config.singleEntity,
        keys: _.map(dataSource.dataKeys, (k) => k.key),
        startTs: startTs,
        timeWindowMs: timeWindowMs,
        aggregationType: timeWindow.aggregation?.type || AggregationType.NONE,
        aggregationIntervalMs: timeWindow.aggregation?.interval || 0,
      }
    } else if (
      timeWindow.timeWindowType === TimeWindowType.HISTORY &&
      timeWindow.history
    ) {
      const { startTs, endTs } = getStartAndEndTsByHistoryWindow(
        timeWindow.history
      )
      return {
        id: id,
        type: QueryType.HISTORY,
        entityID: alias.config.singleEntity,
        keys: _.map(dataSource.dataKeys, (k) => k.key),
        startTs: startTs,
        endTs: endTs,
        aggregationType: timeWindow.aggregation?.type || AggregationType.NONE,
        aggregationIntervalMs: timeWindow.aggregation?.interval || 0,
      }
    }
    return null
  }

  React.useEffect(() => {
    // Get the data sources from the panel config
    const dataSources = panel?.config.dataSources
    if (!dataSources) {
      return
    }
    // Get the timeWindow from the panel config if it is set to use custom time window or get it from the global config
    const timeWindow =
      panel?.config.timeWindow && panel?.config.useCustomTimeWindow
        ? panel.config.timeWindow
        : config.timeWindow
    if (!timeWindow) {
      return
    }
    const queries: QueryState[] = _.reduce<DataSource<any>, QueryState[]>(
      dataSources,
      (acc, datasource, index) => {
        const { entityAliasID } = datasource
        if (!entityAliasID) {
          return acc
        }
        const alias = aliases[entityAliasID.id]
        if (!alias) {
          return acc
        }
        let queries: QueryState[] = []
        if (datasource.latestDataKeys) {
          const query = buildLatestQuery(datasource, alias)
          if (query) {
            query.useStorageTsV2 = useStorageTsV2
            queries.push({ query, datasource, alias })
          }
        }
        if (datasource.dataKeys) {
          if (
            panel?.type === PanelType.SINGLE_VALUE ||
            panel?.type === PanelType.TIME_SERIES_PIE
          ) {
            const query = buildAggregateQuery(
              datasource,
              index,
              alias,
              timeWindow
            )
            if (query) {
              query.useStorageTsV2 = useStorageTsV2
              queries.push({ query, datasource, alias })
            }
          } else {
            const query = buildQuery(datasource, index, alias, timeWindow)
            if (query) {
              query.useStorageTsV2 = useStorageTsV2
              queries.push({ query, datasource, alias })
            }
          }
        }
        console.log('Queries', queries)
        acc.push(...queries)
        return acc
      },
      []
    )
    const regularQueries = queries.filter(
      (q) => isTimeseriesCmd(q.query) || isTimeseriesHistoryCmd(q.query)
    )
    const aggQueries = queries.filter(
      (q) => isTimeseriesAggCmd(q.query) || isTimeseriesHistoryAggCmd(q.query)
    )
    const latestQueries = queries.filter((q) => isLatestTimeseriesCmd(q.query))
    const allQueries = [...regularQueries, ...aggQueries, ...latestQueries]
    setQueries(allQueries)
  }, [
    panel?.config.dataSources,
    config.timeWindow,
    panel?.config.timeWindow,
    useStorageTsV2,
  ])
  /**
   * Fetch data from the backend
   * @param queries
   */
  const fetchData = async (queries: QueryState[]) => {
    if (queries.length === 0) {
      return
    }
    const promises = queries.map(async (currentQuery) => {
      const { datasource, alias, query } = currentQuery
      if (
        query.type === QueryType.HISTORY ||
        query.type === QueryType.REAL_TIME
      ) {
        if (isTimeseriesHistoryAggCmd(query) || isTimeseriesAggCmd(query)) {
          try {
            const data = await getAggTelemetries(query).unwrap()
            setAggregatedData((prev) => {
              return {
                ...prev,
                [query.id]: { datasource, data },
              }
            })
          } catch (err) {
            toast.error('Error fetching aggregated data')
          }
        } else if (isTimeseriesHistoryCmd(query) || isTimeseriesCmd(query)) {
          try {
            const data = await getTelemetries(query).unwrap()
            setTimeSeriesData((prev) => {
              return {
                ...prev,
                [query.id]: { datasource, data },
              }
            })
          } catch (err) {
            toast.error('Error fetching timeseries data')
          }
        }
      } else if (query.type === QueryType.LATEST) {
        if (isLatestTimeseriesCmd(query) && query.keys.length > 0) {
          try {
            const data = await getLatestTelemetries(query).unwrap()
            setLatestData((prev) => {
              console.log('Latest data', data)
              return {
                ...prev,
                [query.id]: { datasource, data },
              }
            })
          } catch (err) {
            toast.error('Error fetching latest data')
          }
        }
      }
      // Return a promise that resolves to null if the query type is not valid
      return Promise.resolve(null)
    })
    await Promise.all(promises)
  }
  // Fetch data when queries change
  React.useEffect(() => {
    if (queries.length === 0) {
      return
    }
    fetchData(queries)
      .then(() => {
        console.log('Data fetched')
      })
      .catch((err) => {
        console.error('Error fetching data', err)
      })
  }, [queries])
  const renderPanelBody = () => {
    switch (panel?.type) {
      case PanelType.TIME_SERIES_LINE:
        return (
          <TimeSeriesLineChart
            options={panel.config.settings}
            timeSeriesData={timeSeriesData}
            timeWindow={config.timeWindow}
            aliases={aliases}
          />
        )
      case PanelType.TIME_SERIES_BAR:
        return (
          <TimeSeriesBarChart
            options={panel.config.settings}
            timeSeriesData={timeSeriesData}
            timeWindow={config.timeWindow}
            aliases={aliases}
          />
        )
      case PanelType.TIME_SERIES_PIE:
        return (
          <TimeSeriesPieChart
            options={panel.config.settings}
            aggregateTimeSeriesData={aggregatedData}
            latestTimeSeriesData={latestData}
            timeWindow={config.timeWindow}
            aliases={aliases}
          />
        )
      case PanelType.GAUGE:
        return (
          <GaugeList
            aliases={aliases}
            timeWindow={config.timeWindow}
            latestTimeSeriesData={latestData}
            options={panel.config.settings}
          />
        )
      case PanelType.SINGLE_VALUE:
        return (
          <SingleValueList
            options={panel.config.settings}
            aliases={aliases}
            latestTimeSeriesData={latestData}
            aggregateTimeSeriesData={aggregatedData}
          />
        )
      case PanelType.SIMPLE_INDICATOR:
        return (
          <SimpleIndicatorList
            options={panel.config.settings}
            aliases={aliases}
            latestTimeSeriesData={latestData}
            aggregateTimeSeriesData={aggregatedData}
          />
        )
      case PanelType.TIMESERIES_TABLE:
        return <TimeSeriesTable timeSeriesData={timeSeriesData} />
      case PanelType.JSON_TABLE:
        return <JsonTable timeSeriesData={timeSeriesData} />
      case PanelType.TIME_SERIES_RADAR:
        return (
          <TimeSeriesPolarChart
            options={panel.config.settings}
            timeSeriesData={timeSeriesData}
            timeWindow={config.timeWindow}
            aliases={aliases}
          />
        )
      default:
        return <span>Panel type is not valid</span>
    }
  }
  if (!panel || !panel.config) {
    return null
  }
  return (
    <Card className={'h-100'}>
      <Card.Header className={'p-0 react-grid-dragHandler cursor-pointer'}>
        <Flex direction={'row'} justifyContent={'between'}>
          <div className={'pt-1 ps-2'}>
            {panel.title && (
              <h5 className={'me-1 d-inline text-secondary'}>{panel.title}</h5>
            )}
            {panel.description && (
              <OverlayTrigger
                overlay={
                  <Tooltip
                    style={{ position: 'fixed' }}
                    id="overlay-trigger-example"
                  >
                    {panel.description}
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={'circle-question'} />
              </OverlayTrigger>
            )}
          </div>
          <div className={''} hidden={!isMouseIn}>
            {isMouseIn}
            {panel.id && <PanelMenu panelID={panel.id} />}
          </div>
        </Flex>
      </Card.Header>
      <Card.Body className={'p-0 h-100 overflow-auto'}>
        {isLoading ? <Spinner /> : null}
        {renderPanelBody()}
      </Card.Body>
    </Card>
  )
}

export default PanelBody
